import { hasFocusClass, postContentClass } from "@repo/fika-blog/src/classes"
import { extensions } from "@repo/fika-blog/src/extensions"
import type { Editor, JSONContent } from "@repo/fika-blog/src/tiptap/core"
import type { IconName } from "@repo/fika-design-system/icon"
import type { UploadFolder } from "@repo/fika-shared/src/upload-folder.js"
import CharacterCount from "@tiptap/extension-character-count"
import TiptapFileHandler from "@tiptap/extension-file-handler"
import Focus from "@tiptap/extension-focus"
import { Placeholder } from "@tiptap/extensions"
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  type JSXElement,
  on,
  type Setter,
  Show,
  untrack,
} from "solid-js"
import { createTiptapEditor, useEditorJSON } from "solid-tiptap"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Dialog } from "@/designSystem/components/dialog"
import { Mini } from "@/designSystem/components/mini/mini"
import { textFieldLabelClass } from "@/designSystem/components/text-field.css"
import { Tooltip } from "@/designSystem/components/tooltip"
import { useTranslatableField } from "@/designSystem/patterns/translatable-field"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/default-json-content"
import { onDrop, onPaste } from "../image-upload"
import { insertImageIntoEditor } from "../insert-image-into-editor"
import { FormatToolbar } from "./menus/format-toolbar"
import {
  editorClass,
  emptyContentClass,
  simpleWysiwygClass,
  toolbarClass,
} from "./simple-wysiwyg.css"
import { getEditSubscribe } from "./subscribe-edit-node"
import { dropCursorClass } from "./tiptap-editor.css"

interface ImageBlockOptions {
  uploadFolder: UploadFolder
}
interface BlockOptions {
  image?: ImageBlockOptions
  horizontalRule?: boolean
  codeBlock?: boolean
  quote?: boolean
  subscribe?: boolean
}

interface SimpleWysiwygEditorProps {
  content: Accessor<string | JSONContent | null>
  onUpdate?: (content: JSONContent) => void
  placeholder?: string
  label?: string
  publicationName: string
  blocks?: BlockOptions
  editable?: boolean
  link?: false
}

export function SimpleWysiwygEditor(props: SimpleWysiwygEditorProps) {
  const parsedContent = createMemo(() => normalizeContent(props.content()))

  const { editor, setEditorRef } = createSimpleEditor({
    content: parsedContent,
    placeholder: props.placeholder,
    publicationName: props.publicationName,
    onUpdate: editor => props.onUpdate?.(editor.getJSON()),
    editable: props.editable,
    link: props.link,
  })

  createEffect(() => {
    const editable = props.editable ?? true
    editor()?.setEditable(editable)
  })

  return (
    <WysiwygEditor
      editor={editor()}
      label={props.label}
      setEditorRef={setEditorRef}
      blocks={props.blocks}
      link={props.link}
    />
  )
}

interface SimpleWysiwygDialogProps {
  content: Accessor<string | JSONContent | null>
  title: string
  onSave: (content: JSONContent) => Promise<void> | void
  placeholder?: string
  editLabel?: string
  label?: string
  isSaving?: boolean
  dialogContent?: JSXElement
  publicationName: string
  blocks?: BlockOptions
  isDirty?: () => boolean
}

export function SimpleWysiwyg(props: SimpleWysiwygDialogProps) {
  const { LL } = useI18nContext()
  const translatableContext = useTranslatableField()
  const [isOpen, setIsOpen] = createSignal(false)

  function handleDialogOpenChange(open: boolean) {
    setIsOpen(open)
    if (!open) translatableContext?.resetLocale()
  }
  const parsedContent = createMemo(() => normalizeContent(props.content()))

  const { editor, setEditorRef } = createSimpleEditor({
    content: () => untrack(() => parsedContent()),
    imageBlock: props.blocks?.image,
    placeholder: props.placeholder,
    autoFocusOnCreate: true,
    publicationName: props.publicationName,
    onBlur: editor => {
      if (!translatableContext) return

      translatableContext.syncContent(
        JSON.stringify(editor.getJSON()),
        translatableContext.currentLocale(),
      )
    },
  })

  const { editor: previewEditor, setEditorRef: setPreviewEditorRef } =
    createSimpleEditor({
      content: () => untrack(() => parsedContent()),
      editable: false,
      publicationName: props.publicationName,
    })

  const contentKey = () => translatableContext?.currentLocale()
  const [prevContentKey, setPrevContentKey] = createSignal(contentKey())

  createEffect(
    on(
      () => [parsedContent(), contentKey()] as const,
      ([content, newKey]) => {
        const oldKey = prevContentKey()

        if (oldKey && oldKey !== newKey && editor() && translatableContext) {
          const currentJson = editor()!.getJSON()
          translatableContext.syncContent(JSON.stringify(currentJson), oldKey)
        }
        setPrevContentKey(newKey)

        editor()?.commands.setContent(content)
        previewEditor()?.commands.setContent(content)
      },
      { defer: true },
    ),
  )

  const getJSON = useEditorJSON(() => editor())

  const contentIsDirty = createMemo(() => {
    const json = getJSON()
    if (!json) return false
    return JSON.stringify(json) !== JSON.stringify(parsedContent())
  })
  const isDirty = createMemo(
    () =>
      (props.isDirty?.() ?? false) ||
      (translatableContext?.isDirty() ?? false) ||
      contentIsDirty(),
  )

  async function handleSave() {
    const json = getJSON()
    if (!json) return
    await props.onSave(json)
    handleDialogOpenChange(false)
  }

  return (
    <Box gap="16">
      <Show
        when={props.content()}
        fallback={<div class={emptyContentClass}>No content</div>}
      >
        <div ref={setPreviewEditorRef} />
      </Show>
      <Box justifyContent="flex-end" flexDirection="row">
        <Button
          kind="ghost"
          hue="amber"
          iconName="pencil"
          onClick={() => setIsOpen(true)}
        >
          {props.editLabel ?? LL().common.edit()}
        </Button>
      </Box>

      <Dialog
        open={isOpen()}
        onOpenChange={handleDialogOpenChange}
        title={props.title}
        size="medium"
        footer={
          <>
            <Button
              kind="ghost"
              hue="amber"
              onClick={() => handleDialogOpenChange(false)}
            >
              {LL().common.close()}
            </Button>
            <Button
              hue="amber"
              onClick={handleSave}
              disabled={!isDirty() || props.isSaving}
              active={props.isSaving}
            >
              {LL().common.save()}
            </Button>
          </>
        }
      >
        <Show when={props.dialogContent}>{props.dialogContent}</Show>
        <WysiwygEditor
          editor={editor()}
          label={props.label}
          setEditorRef={setEditorRef}
          blocks={props.blocks}
        />
      </Dialog>
    </Box>
  )
}

function SimpleToolbar(props: {
  editor: Editor
  blocks?: BlockOptions
  hideLink?: boolean
}) {
  const { LL } = useI18nContext()

  function insertHorizontalRule() {
    props.editor.chain().focus().setHorizontalRule().run()
  }

  function setCodeBlock() {
    props.editor.chain().focus().setNode("codeBlock").run()
    setTimeout(() => {
      const { state } = props.editor
      const { selection } = state
      const { from } = selection
      props.editor.chain().setTextSelection(from).focus().run()
    }, 0)
  }

  function toggleBlockquote() {
    props.editor.chain().focus().toggleBlockquote().run()
  }

  function addSubscribe() {
    props.editor.chain().focus().insertSubscribe().run()
  }

  const blockItems = createMemo(() => {
    const items: { value: string; label: string; iconName: IconName }[] = []

    if (props.blocks?.horizontalRule)
      items.push({
        value: "horizontalRule",
        label: LL().editor.horizontalRule(),
        iconName: "minus",
      })
    if (props.blocks?.codeBlock)
      items.push({
        value: "codeBlock",
        label: LL().editor.code(),
        iconName: "code",
      })
    if (props.blocks?.quote)
      items.push({
        value: "quote",
        label: LL().editor.quote(),
        iconName: "quote",
      })
    if (props.blocks?.subscribe)
      items.push({
        value: "subscribe",
        label: LL().editor.subscribe(),
        iconName: "mail",
      })

    return items
  })

  const hasBlocks = createMemo(() => blockItems().length > 0)

  function handleBlockSelect(value: string | null) {
    if (!value) return
    switch (value) {
      case "horizontalRule":
        insertHorizontalRule()
        break
      case "codeBlock":
        setCodeBlock()
        break
      case "quote":
        toggleBlockquote()
        break
      case "subscribe":
        addSubscribe()
        break
    }
  }

  async function handleImageUpload(file: File, uploadFolder: UploadFolder) {
    await insertImageIntoEditor({
      editor: props.editor,
      file,
      uploadFolder,
    })
  }

  return (
    <Mini.Menu class={toolbarClass}>
      <Mini.Section>
        <FormatToolbar editor={() => props.editor} hideLink={props.hideLink} />
        <Show when={hasBlocks()}>
          <Show when={props.blocks?.image}>
            {image => (
              <Tooltip description={LL().editor.image()}>
                <Mini.ImagePicker
                  onChange={file =>
                    handleImageUpload(file, image().uploadFolder)
                  }
                  iconName="image"
                />
              </Tooltip>
            )}
          </Show>
          <Mini.Divider />
          <Tooltip description={LL().editor.insertBlock()}>
            <Mini.Select
              items={blockItems()}
              value={blockItems()[0]?.value}
              onChange={handleBlockSelect}
              fixedIcon="ellipsis"
              hideValue
              portal={false}
            />
          </Tooltip>
        </Show>
      </Mini.Section>
    </Mini.Menu>
  )
}

function normalizeContent(content: string | JSONContent | null): JSONContent {
  if (!content) return JSON.parse(defaultJsonContent)
  if (typeof content !== "string") return content

  try {
    return JSON.parse(content) as JSONContent
  } catch {
    return JSON.parse(defaultJsonContent)
  }
}

interface CreateSimpleEditorProps {
  content: Accessor<JSONContent | null>
  onUpdate?: (editor: Editor) => void
  onBlur?: (editor: Editor) => void
  imageBlock?: ImageBlockOptions
  placeholder?: string
  autoFocusOnCreate?: boolean
  editable?: boolean
  publicationName: string
  link?: false
}

function createSimpleEditor(props: CreateSimpleEditorProps) {
  const { LL } = useI18nContext()
  const [editorRef, setEditorRef] = createSignal<HTMLDivElement>()

  const editorInstance = createTiptapEditor(() => {
    const uploadFolder = props.imageBlock?.uploadFolder
    return {
      element: editorRef()!,
      content: untrack(() => props.content?.()),
      autofocus: false,
      onUpdate: ({ editor }) => {
        props.onUpdate?.(editor)
      },
      onBlur: ({ editor }) => {
        props.onBlur?.(editor)
      },
      editable: props.editable ?? true,
      editorProps: {
        attributes: {
          class: `${editorClass} ${postContentClass}`,
        },
      },
      extensions: [
        ...extensions({
          dropcursor: {
            color: false,
            width: 2,
            class: dropCursorClass,
          },
          Subscribe: getEditSubscribe({ userName: props.publicationName }),
          link: props.link,
        }),
        Placeholder.configure({
          placeholder: props.placeholder ?? LL().editor.defaultPlaceholder(),
        }),
        Focus.configure({ className: hasFocusClass, mode: "deepest" }),
        ...(uploadFolder
          ? [
              TiptapFileHandler.configure({
                onDrop: (editor, files, pos) => {
                  onDrop({ editor, files, pos, uploadFolder })
                },
                onPaste: (editor, files) => {
                  return onPaste({ editor, files, uploadFolder })
                },
              }),
            ]
          : []),
        CharacterCount,
      ],
    }
  })

  return { editor: editorInstance, setEditorRef }
}

function WysiwygEditor(props: {
  editor?: Editor
  label?: string
  setEditorRef: Setter<HTMLDivElement | undefined>
  blocks?: BlockOptions
  link?: false
}) {
  return (
    <Box gap="8">
      <Show when={props.label}>
        {label => <div class={textFieldLabelClass}>{label()}</div>}
      </Show>
      <div class={simpleWysiwygClass}>
        <Show when={props.editor}>
          {editor => (
            <SimpleToolbar
              editor={editor()}
              blocks={props.blocks}
              hideLink={props.link === false}
            />
          )}
        </Show>
        <div class={editorClass} ref={props.setEditorRef} />
      </div>
    </Box>
  )
}
